import axios, { AxiosResponse, Method } from "axios";
import { BASE_API_URL, DOCUMENT_HANDLER_API_URL } from "../config";

import StorebrandKeycloak from "@storebrand-digital/storebrand-auth";
import store from "../store";
import { requestSigningAborted } from "../store/signing/slice";
import { SignOrderResponse } from "../store/signing/types/SignOrderResponse";
import { chunkArray } from "./arrayUtils";
import { sleep } from "./asyncUtils";

interface RequestType {
  method?: Method;
  requestBody?: any;
  url: string;
  open?: boolean;
}

/**
 * IMPORTANT: Below variable only be used for ex. environment check.
 * Do not use to make requests within app.
 */
const PRODUCTION_API_ROOT_URL = "https://api.storebrand.no";

// Cache the API root so we don't have to ask
// before every request.
let apiRoot: string | undefined;

const getApiRoot = async (url: string): Promise<string> => {
  // If the api call starts with /api, we should use the current
  // hostname as the api root
  if (window.env.environment === "development") {
    return "";
  }

  if (url.includes("/api")) {
    return "";
  }
  // Use cached value
  if (typeof apiRoot !== "undefined") {
    return apiRoot;
  }

  try {
    const { data } = await axios.get("/api.json");
    apiRoot = data.apihome;
  } catch (e) {
    apiRoot = PRODUCTION_API_ROOT_URL;
  }
  return apiRoot ?? "";
};

const constructApiUrl = async (url: string): Promise<string> => {
  return `${await getApiRoot(url)}${url}`;
};

export const createRequest = async <T = any>({
  method = "get",
  requestBody = {},
  url,
}: RequestType): Promise<AxiosResponse<T>> =>
  axios.request({
    method,
    url: await constructApiUrl(url),
    data: requestBody,
  });

export const createPDFDownloadRequest = async ({
  method = "get",
  requestBody = {},
  url,
}: RequestType): Promise<AxiosResponse<any>> =>
  axios.request({
    method,
    url: await constructApiUrl(url),
    data: requestBody,
    headers: {
      "Content-Type": "application/pdf",
    },
    responseType: "blob",
  });

export const createPDFUploadRequest = async <T = any>({
  method = "post",
  requestBody = {},
  url,
}: RequestType): Promise<AxiosResponse<T>> =>
  axios.request({
    method,
    url: await constructApiUrl(url),
    data: requestBody,
    headers: {
      "content-type": "multipart/form-data",
    },
  });

const MAX_DOCUMENT_HANDLER_CHUNK_SIZE = 5;

export interface DocumentUploadResponse {
  fileName: string;
  fileId: string;
}

export async function uploadDocuments(
  files: File[],
  scanTimeout = 30000,
): Promise<DocumentUploadResponse[]> {
  const uploadedFileChunks = await Promise.all(
    chunkArray(files, MAX_DOCUMENT_HANDLER_CHUNK_SIZE).map((fileChunk) =>
      uploadDocumentChunk(fileChunk, scanTimeout),
    ),
  );
  return uploadedFileChunks.flat();
}

async function uploadDocumentChunk(
  files: File[],
  scanTimeout: number,
): Promise<DocumentUploadResponse[]> {
  try {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append("file", file);
    });

    const response = await createPDFUploadRequest<DocumentUploadResponse[]>({
      method: "post",
      url: `${DOCUMENT_HANDLER_API_URL}/file`,
      requestBody: formData,
    });

    const uploadedFiles = response.data;
    await Promise.all(
      uploadedFiles.map(({ fileId }) =>
        waitUntilDocumentIsScanned(fileId, scanTimeout),
      ),
    );
    return uploadedFiles;
  } catch (error) {
    throw new Error("Oi, filopplastingen kunne ikke gjennomføres. Prøv igjen.");
  }
}

async function waitUntilDocumentIsScanned(
  fileId: string,
  timeout: number,
): Promise<void> {
  let timedOut = false;
  setTimeout(() => {
    timedOut = true;
  }, timeout);

  while (!(await checkDocumentScanStatus(fileId))) {
    if (timedOut) {
      throw new Error("Scanning av fil timet ut.");
    }
    await sleep(2500);
  }
}

async function checkDocumentScanStatus(fileId: string): Promise<boolean> {
  const preferredUsername = (await StorebrandKeycloak.getIdTokenParsed())
    ?.preferred_username;
  if (!preferredUsername) {
    throw new Error(
      "Kan ikke sjekke status på opplastet fil: 'preferred_username' mangler i brukertoken",
    );
  }

  try {
    const response = await createRequest({
      url: `${DOCUMENT_HANDLER_API_URL}/files/scanned/${preferredUsername}/${fileId}`,
    });

    switch (response.status) {
      case 200:
        return true;
      case 204:
        return false;
      default:
        throw new Error(`Uforventet status fra fil scan ${response.status}`);
    }
  } catch (error) {
    throw new Error("Scanning av opplastet fil feilet.");
  }
}

export async function isProductionEnvironment(): Promise<boolean> {
  return window.env.environment === "production";
}

/**
 * Accepts a http request returned from axios interceptor to attemtp to extract the caseId
 * and delete the current signing order on the case. This situation could occur if the user
 * uses the back button in the browser to exit the Signicat process or if multiple browser
 * tabs are used. If a request to change an answer is sent it is inmportant that we
 * invalidate the previous signing order
 */
export async function removeSigningOrder(request: XMLHttpRequest) {
  const { responseURL } = request;
  if (responseURL) {
    const caseIds = responseURL.match(/EDH-\d{4}-\d{6}/gi);
    caseIds?.forEach(async (caseId) => {
      const { data }: AxiosResponse<SignOrderResponse> = await createRequest({
        url: `${BASE_API_URL}/cases/${caseId}/signing`,
      });

      const { signingReference } = data;

      if (signingReference) {
        store.dispatch(
          requestSigningAborted({
            caseId: caseId,
            signingReference: signingReference,
          }),
        );
      }
    });
  }
}
